import type { AgenticLogicSurface } from "./surface";

// ---------------------------------------------------------------------------
// Which request context a surface can actually supply.
//
// DOCUMENTATION-AS-DATA. Nothing here is executed against rule code: the
// platform does not infer what a rule reads, and human-authored code is
// trusted. This table exists so the ONE statement of per-surface availability
// can be rendered into the rule-authoring contract instead of being re-worded
// by hand in the editor types, the workspace panel and two prompt files.
//
// It is derived from the only three producers of a serving request:
//   onsite         web/node-backend/src/modules/components/components.controller.ts
//   checkout       web/node-backend/src/modules/checkout-blocks/checkout-blocks.controller.ts
//   post-purchase  the same controller's post-purchase offer resolver
// Change it only alongside one of those.
// ---------------------------------------------------------------------------

/**
 * The request blocks whose presence depends on the surface. Deliberately not
 * every request key: `surface`, `placementId`, `shipping` and `evaluatedAt` are
 * supplied on every surface, so they carry no availability question and stating
 * them here would invite the table to be read as the full field list.
 */
export const AgenticLogicSurfaceInputFieldList = [
  "cart",
  "order",
  "pageProduct",
  "customer",
] as const;

export type AgenticLogicSurfaceInputField =
  (typeof AgenticLogicSurfaceInputFieldList)[number];

/**
 * How reliably a surface supplies one BLOCK of request context.
 *
 * The three-way split is the whole point. `sometimes` is NOT a weaker `never`:
 * it means the block arrives for some shoppers and is null for others, so the
 * rule's job is the null-check, and such a rule must stay legal — the request
 * contract already models every context block as a required key with a
 * nullable value. Only `never` is a rule that cannot fire — the data does not
 * exist on that surface and no shopper state will produce it.
 *
 * This table is BLOCK-LEVEL ONLY. It says nothing about whether a particular
 * sub-field inside a block that IS sent ever carries a value; asserting
 * otherwise is how a rule keyed on `customer.ordersCount` at checkout shipped
 * with a dead branch. Sub-field gaps live in
 * {@link AGENTIC_LOGIC_SURFACE_FIELD_CAVEATS}.
 */
export const AgenticLogicFieldAvailabilityList = [
  "always",
  "sometimes",
  "never",
] as const;

export type AgenticLogicFieldAvailability =
  (typeof AgenticLogicFieldAvailabilityList)[number];

/**
 * `satisfies` rather than a bare annotation so adding a surface to
 * {@link AgenticLogicSurfaceList} without declaring what it can supply is a
 * compile error here, rather than an `undefined` that renders as a blank row in
 * the authoring contract. Same guarantee `AGENTIC_LOGIC_SURFACE_BASE_CAP_MS`
 * gives for evaluation budgets.
 */
export const AGENTIC_LOGIC_SURFACE_FIELD_AVAILABILITY = {
  onsite: {
    // Null until the storefront reports a cart currency.
    cart: "sometimes",
    order: "never",
    // Only on a product page; null everywhere else onsite.
    pageProduct: "sometimes",
    // The storefront is unauthenticated, so onsite sends no identity at all.
    customer: "never",
  },
  checkout: {
    cart: "always",
    order: "never",
    pageProduct: "never",
    // Identity only, and only for a signed-in buyer, from the verified session
    // token's `sub`. See AGENTIC_LOGIC_SURFACE_FIELD_CAVEATS for what the
    // block does NOT carry.
    customer: "sometimes",
  },
  "post-purchase": {
    // The order has been placed; there is no cart left to read.
    cart: "never",
    order: "always",
    pageProduct: "never",
    customer: "never",
  },
} as const satisfies Record<
  AgenticLogicSurface,
  Record<AgenticLogicSurfaceInputField, AgenticLogicFieldAvailability>
>;

/**
 * The HAND-WRITTEN half of the sub-field caveat set.
 *
 * Sub-field gaps inside a block a surface DOES send. Block-level availability
 * cannot express "the object arrives but this key inside it is never filled",
 * and that gap shipped a rule whose repeat-customer branch was dead code.
 * Each entry names the dead sub-field AND the read that answers the same
 * question, because an author told only "do not use X" picks the next-cheapest
 * request field rather than paying for a read.
 *
 * The OTHER half — the per-line provenance flags — is GENERATED from
 * {@link AGENTIC_LOGIC_SURFACE_LINE_FLAG_FIDELITY}, because a prose-only table
 * cannot be asked "is every dead sub-field disclosed?" and that is exactly the
 * invariant this family of defects needs. Both halves render through the same
 * `caveatClauses` in `request-contract.ts`.
 */
export const AGENTIC_LOGIC_SURFACE_FIELD_CAVEATS = {
  checkout: {
    customer:
      "`customer.ordersCount` is NULL when Shopify's protected-customer-data grant does not cover it or the buyer's identity could not be verified against the session token — null-check it before branching on it. Call `sdk.getCustomer()` for lifetime spend, tags, account age and last order date; it is a budget-guarded Admin read, pinned to one call per evaluation, and it resolves whenever `request.customer` is non-null.",
  },
} as const satisfies Partial<
  Record<
    AgenticLogicSurface,
    Partial<Record<AgenticLogicSurfaceInputField, string>>
  >
>;

/**
 * The provenance flags every cart and order line carries
 * (`AgenticLogicCartLineFlags`), as values rather than only a type, so a
 * per-flag table can be made total.
 */
export const AgenticLogicCartLineFlagList = [
  "giftCard",
  "syntheticGift",
  "bundleChild",
  "igUpsell",
] as const;

export type AgenticLogicCartLineFlag =
  (typeof AgenticLogicCartLineFlagList)[number];

/**
 * What a surface's producer can actually SAY about one line flag.
 *
 * `observed` is the only fidelity a rule may key on. The other two are dead
 * clauses wearing a live type: the flag is typed `boolean | null` on every
 * surface, so a `!== true` predicate compiles, runs, and can never change the
 * outcome — which is how a checkout gift-card exclusion shipped as "delivered"
 * while excluding nothing.
 */
export const AgenticLogicLineFlagFidelityList = [
  /** The producer reads a real marker; true and false both occur. */
  "observed",
  /** No marker exists on this surface; the flag is never non-null. */
  "always-null",
  /** The producer answers, but the true case cannot arise here. */
  "always-false",
] as const;

export type AgenticLogicLineFlagFidelity =
  (typeof AgenticLogicLineFlagFidelityList)[number];

/**
 * The ONE machine-readable statement of which surface can answer which line
 * flag — the fact that was previously spread across four producer comments
 * that no renderer reads.
 *
 * `satisfies` is load-bearing exactly as it is for availability: a new surface
 * or a new flag is a BUILD ERROR until its fidelity is declared, rather than an
 * `undefined` that silently renders as "no caveat".
 *
 * Each non-`observed` entry names the producer line it mirrors. Changing that
 * line means deleting the entry here, and the drift guard in
 * `web/node-backend/__tests__/unit/lib/agentic-logic-surfaces/cart-line-flags.spec.ts`
 * — which drives the real producers — fails until you do.
 */
export const AGENTIC_LOGIC_SURFACE_LINE_FLAG_FIDELITY = {
  onsite: {
    // `item.gift_card`, a required boolean on /cart.js.
    giftCard: "observed",
    syntheticGift: "observed",
    // ig-utils/src/cart-producers/ajax-cart.ts:42 — /cart.js lists no bundle
    // component lines, so the producer answers `false` for every line.
    bundleChild: "always-false",
    igUpsell: "observed",
  },
  checkout: {
    // ig-utils/src/cart-producers/checkout-lines.ts:117 — the key is omitted,
    // and cart-line-flags.ts:81 turns that absence into `null`.
    giftCard: "always-null",
    syntheticGift: "observed",
    // Lines nested in a parent's `lineComponents` ARE the children.
    bundleChild: "observed",
    igUpsell: "observed",
  },
  "post-purchase": {
    // node-backend/src/lib/agentic-logic-surfaces/normalize-request.ts:200 —
    // an order line is derived from `{ igLineMarkers }` only, so neither
    // evidence key is ever passed.
    giftCard: "always-null",
    syntheticGift: "observed",
    bundleChild: "always-null",
    igUpsell: "observed",
  },
} as const satisfies Record<
  AgenticLogicSurface,
  Record<AgenticLogicCartLineFlag, AgenticLogicLineFlagFidelity>
>;

/**
 * Which request block carries a surface's LINES — `request.cart` or
 * `request.order`.
 *
 * DERIVED rather than a fourth hand-maintained table: it is whichever of
 * `cart`/`order` is not `never` for that surface. That is what puts the
 * post-purchase line-flag caveat on `request.order` and the other two on
 * `request.cart` without anyone re-deciding it per surface, and it throws
 * rather than guessing if a future surface sends both or neither.
 */
export function agenticLogicLineBlock(
  surface: AgenticLogicSurface
): Extract<AgenticLogicSurfaceInputField, "cart" | "order"> {
  const blocks = (["cart", "order"] as const).filter(
    (block) =>
      AGENTIC_LOGIC_SURFACE_FIELD_AVAILABILITY[surface][block] !== "never"
  );
  if (blocks.length !== 1) {
    throw new Error(
      `agenticLogicLineBlock: ${surface} declares ${blocks.length} line blocks (expected exactly one of cart/order)`
    );
  }
  return blocks[0] as Extract<AgenticLogicSurfaceInputField, "cart" | "order">;
}

/**
 * The fields NO surface in `surfaces` can supply — what a rule targeting those
 * surfaces must not build on, because the data will not be there on any
 * request. A field that is `sometimes` on even one declared surface is absent
 * from this list: it is available, and the rule's job is to handle the null.
 */
export function agenticLogicUnavailableFields(
  surfaces: readonly AgenticLogicSurface[]
): AgenticLogicSurfaceInputField[] {
  if (surfaces.length === 0) {
    return [];
  }
  return AgenticLogicSurfaceInputFieldList.filter((field) =>
    surfaces.every(
      (surface) =>
        AGENTIC_LOGIC_SURFACE_FIELD_AVAILABILITY[surface][field] === "never"
    )
  );
}
